/*
 * Booking wizard controller (Book an Appointment page).
 * Same progressive step pattern as design-studio-wizard.js, but there is no
 * Shopify contact form: the last two steps pick a meeting type (Virtual /
 * In-person) and embed the matching Cal.com event type inline via the official
 * Cal Embed API. Every earlier answer is passed into the booking's notes field,
 * and Cal's bookingSuccessful event flips the wizard to the confirmation view.
 *
 * The embed's prefill config is fixed at iframe creation, so if the visitor
 * goes back and changes an answer (or the meeting type) the calendar is rebuilt
 * under a fresh namespace instead of reusing a stale one.
 */
(function () {
  function loadCalShim(origin) {
    if (window.Cal) return;
    (function (C, A, L) {
      var p = function (a, ar) { a.q.push(ar); };
      var d = C.document;
      C.Cal = C.Cal || function () {
        var cal = C.Cal;
        var ar = arguments;
        if (!cal.loaded) {
          cal.ns = {};
          cal.q = cal.q || [];
          d.head.appendChild(d.createElement('script')).src = A;
          cal.loaded = true;
        }
        if (ar[0] === L) {
          var api = function () { p(api, arguments); };
          var namespace = ar[1];
          api.q = api.q || [];
          if (typeof namespace === 'string') {
            cal.ns[namespace] = cal.ns[namespace] || api;
            p(cal.ns[namespace], ar);
            p(cal, ['initNamespace', namespace]);
          } else {
            p(cal, ar);
          }
          return;
        }
        p(cal, ar);
      };
    })(window, origin.replace(/\/+$/, '') + '/embed/embed.js', 'init');
  }

  /*
   * Normalize a merchant-entered event link. Accepts a full URL
   * ("https://waco-diamonds.cal.com/sales-team/virtual"), a schemeless domain
   * form ("waco-diamonds.cal.com/sales-team/virtual"), or a bare path
   * ("sales-team/virtual"). The embed needs them split: calLink is the PATH
   * only, and the iframe origin must be the link's own domain (org subdomains
   * like waco-diamonds.cal.com serve their own booking pages).
   */
  function parseCalLink(raw, defaultOrigin) {
    raw = (raw || '').trim();
    if (!raw) return null;
    var m = raw.match(/^https?:\/\/([^\/?#]+)\/?([^?#]*)/i);
    if (!m) {
      var m2 = raw.match(/^([a-z0-9-]+(?:\.[a-z0-9-]+)+)\/([^?#]+)/i);
      if (m2 && /(^|\.)cal\.com$/i.test(m2[1])) m = [raw, m2[1], m2[2]];
    }
    if (m) {
      var path = (m[2] || '').replace(/^\/+|\/+$/g, '');
      if (!path) return null;
      return { origin: 'https://' + m[1], link: path };
    }
    return { origin: defaultOrigin, link: raw.replace(/^\/+|\/+$/g, '') };
  }

  function track(event, data) {
    try {
      window.dataLayer = window.dataLayer || [];
      var payload = { event: event };
      if (data) { for (var k in data) payload[k] = data[k]; }
      window.dataLayer.push(payload);
    } catch (e) { /* analytics must never break booking */ }
  }

  function initWizard(container) {
    if (container.__bkInit) return;
    container.__bkInit = true;

    var cfg = {
      origin: container.getAttribute('data-bk-origin') || 'https://app.cal.com',
      virtual: container.getAttribute('data-bk-virtual') || '',
      inperson: container.getAttribute('data-bk-inperson') || '',
      brand: container.getAttribute('data-bk-brand') || '#333624',
      brandDark: container.getAttribute('data-bk-brand-dark') || '#f9e49d',
      hideDetails: container.getAttribute('data-bk-hide-details') === 'true'
    };

    var steps = Array.prototype.slice.call(container.querySelectorAll('[data-bk-step]'));
    var progress = container.querySelector('[data-bk-progress]');
    var stepLabel = container.querySelector('[data-bk-steplabel]');
    var stepTitle = container.querySelector('[data-bk-steptitle]');
    var backBtn = container.querySelector('[data-bk-back]');
    var nextBtn = container.querySelector('[data-bk-next]');
    var typeInputs = Array.prototype.slice.call(container.querySelectorAll('[data-bk-typeinput]'));
    var meetInputs = Array.prototype.slice.call(container.querySelectorAll('[data-bk-meetinput]'));
    var conditionalStep = container.querySelector('[data-bk-step="size"]');
    var conditionalValue = conditionalStep ? conditionalStep.getAttribute('data-bk-conditional') : null;
    var recapEl = container.querySelector('[data-bk-recap]');
    var calMount = container.querySelector('[data-bk-cal-mount]');
    var calBox = container.querySelector('[data-bk-cal]');
    var calFallback = container.querySelector('[data-bk-cal-fallback]');

    if (!steps.length || !nextBtn || !calMount) return;

    container.classList.add('cj-wiz--js');
    var idx = 0;
    var embedSeq = 0;
    var currentEmbed = null; // { key, ns }

    function checkedValue(name) {
      var el = container.querySelector('[name="' + name + '"]:checked');
      return el ? el.value.trim() : '';
    }
    function fieldValue(name) {
      var el = container.querySelector('[name="' + name + '"]');
      return el ? el.value.trim() : '';
    }
    function selectedType() { return checkedValue('bk-piece'); }
    function selectedMeet() { return checkedValue('bk-meet'); }
    function selectedMeetLabel() {
      var el = container.querySelector('[name="bk-meet"]:checked');
      return el ? (el.getAttribute('data-bk-meetlabel') || el.value) : '';
    }

    // Active step list: drop the conditional ring-size step unless its type is chosen.
    function activeSteps() {
      return steps.filter(function (el) {
        var cond = el.getAttribute('data-bk-conditional');
        if (!cond) return true;
        return selectedType() === cond;
      });
    }
    function canAdvance(stepEl) {
      var id = stepEl.getAttribute('data-bk-step');
      if (id === 'type') return !!selectedType();
      if (id === 'meet') return !!selectedMeet();
      return true;
    }

    function render() {
      var list = activeSteps();
      if (idx >= list.length) idx = list.length - 1;
      if (idx < 0) idx = 0;
      var cur = list[idx];
      var curId = cur.getAttribute('data-bk-step');

      steps.forEach(function (el) { el.classList.toggle('is-active', el === cur); });

      var total = list.length;
      if (stepLabel) stepLabel.textContent = 'Step ' + (idx + 1) + ' of ' + total;
      if (stepTitle) stepTitle.textContent = cur.getAttribute('data-bk-title') || '';
      if (progress) progress.style.width = Math.round(((idx + 1) / total) * 100) + '%';
      if (backBtn) backBtn.style.visibility = idx > 0 ? 'visible' : 'hidden';

      var ok = canAdvance(cur);
      nextBtn.classList.toggle('is-disabled', !ok);
      nextBtn.setAttribute('aria-disabled', ok ? 'false' : 'true');

      var isSchedule = curId === 'schedule';
      container.classList.toggle('cj-wiz--schedule', isSchedule);
      if (isSchedule) ensureEmbed();

      track('waco_booking_step', { step: curId, step_index: idx + 1 });
    }

    function buildNotes() {
      var lines = [];
      function add(k, v) { if (v) lines.push(k + ': ' + v); }
      add('Piece type', selectedType());
      if (selectedType() === conditionalValue) add('Ring size', checkedValue('bk-size'));
      add('Inspiration', fieldValue('bk-inspo'));
      add('Their idea', fieldValue('bk-idea'));
      add('Budget', checkedValue('bk-budget'));
      add('Timeline', checkedValue('bk-timeline'));
      add('Meeting', selectedMeetLabel());
      lines.push('— Submitted via the Book an Appointment wizard on wacodiamonds.com');
      return lines.join('\n');
    }

    function renderRecap() {
      if (!recapEl) return;
      var chips = [selectedMeetLabel(), selectedType(), checkedValue('bk-size') && 'Size ' + checkedValue('bk-size'), checkedValue('bk-budget'), checkedValue('bk-timeline')].filter(Boolean);
      recapEl.innerHTML = '';
      chips.forEach(function (c) {
        var el = document.createElement('span');
        el.className = 'bk-recap__chip';
        el.textContent = c;
        recapEl.appendChild(el);
      });
      recapEl.hidden = chips.length === 0;
    }

    function onBooked() {
      container.classList.add('cj-wiz--done');
      container.scrollIntoView({ behavior: 'smooth', block: 'start' });
      track('waco_booking_confirmed', { meeting_type: selectedMeet() });
    }

    function ensureEmbed() {
      renderRecap();
      var meet = selectedMeet();
      var target = parseCalLink(meet === 'inperson' ? cfg.inperson : cfg.virtual, cfg.origin);
      if (!target) {
        if (calBox) calBox.hidden = true;
        if (calFallback) calFallback.hidden = false;
        return;
      }
      if (calBox) calBox.hidden = false;
      if (calFallback) calFallback.hidden = true;

      var notes = buildNotes();
      var key = meet + '|' + notes;
      if (currentEmbed && currentEmbed.key === key) return;

      calMount.innerHTML = '';
      var frame = document.createElement('div');
      frame.className = 'bk-cal__frame';
      calMount.appendChild(frame);

      var ns = 'wacobk' + (++embedSeq);
      loadCalShim(target.origin);
      window.Cal('init', ns, { origin: target.origin });
      window.Cal.ns[ns]('inline', {
        elementOrSelector: frame,
        calLink: target.link,
        layout: 'month_view',
        config: { layout: 'month_view', theme: 'light', notes: notes }
      });
      // Theme the INSIDE of the iframe to the page's cream palette so the
      // calendar blends into the page instead of sitting in a white box
      // (values mirror the .waco-bk palette in waco-booking-wizard.css).
      window.Cal.ns[ns]('ui', {
        theme: 'light',
        hideEventTypeDetails: cfg.hideDetails,
        layout: 'month_view',
        cssVarsPerTheme: {
          light: {
            'cal-brand': cfg.brand,
            'cal-bg': '#fffdf5',
            'cal-bg-muted': '#f3f4ec',
            'cal-bg-subtle': '#f3f4ec',
            'cal-bg-emphasis': '#efece1',
            'cal-border': '#e6e3d8',
            'cal-border-default': '#e6e3d8',
            'cal-border-subtle': '#e6e3d8',
            'cal-border-booker': '#fffdf5',
            'cal-border-booker-width': '0px'
          },
          dark: { 'cal-brand': cfg.brandDark }
        }
      });
      window.Cal.ns[ns]('on', { action: 'bookingSuccessful', callback: onBooked });
      currentEmbed = { key: key, ns: ns };
      track('waco_booking_meet_type', { meeting_type: meet });
    }

    function goNext() {
      var list = activeSteps();
      var cur = list[idx];
      if (!canAdvance(cur)) return;
      idx = Math.min(idx + 1, activeSteps().length - 1);
      render();
    }
    function goBack() { idx = Math.max(idx - 1, 0); render(); }

    nextBtn.addEventListener('click', function (e) { e.preventDefault(); goNext(); });
    if (backBtn) backBtn.addEventListener('click', goBack);
    typeInputs.forEach(function (el) { el.addEventListener('change', render); });
    meetInputs.forEach(function (el) { el.addEventListener('change', render); });

    Array.prototype.forEach.call(container.querySelectorAll('[data-bk-advance]'), function (b) {
      b.addEventListener('click', goNext);
    });
    Array.prototype.forEach.call(container.querySelectorAll('[data-bk-set-advance]'), function (b) {
      b.addEventListener('click', function () {
        var name = b.getAttribute('data-bk-set-advance');
        var value = b.getAttribute('data-bk-value');
        var field = container.querySelector('[name="' + name + '"][value="' + value.replace(/"/g, '\\"') + '"]');
        if (field) field.checked = true;
        goNext();
      });
    });

    var resetBtn = container.querySelector('[data-bk-reset]');
    if (resetBtn) resetBtn.addEventListener('click', function () {
      container.classList.remove('cj-wiz--done');
      currentEmbed = null; // force a fresh calendar, not Cal's stale confirmation view
      var list = activeSteps();
      for (var i = 0; i < list.length; i++) {
        if (list[i].getAttribute('data-bk-step') === 'meet') { idx = i; break; }
      }
      render();
    });

    render();
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll('[data-bk-wizard]'), initWizard);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
  // Re-init in the Shopify theme editor when the section reloads.
  document.addEventListener('shopify:section:load', function (e) { init(e && e.target ? e.target : document); });
})();
